import axios from 'axios';

// ── Config loader ──────────────────────────────────────────────────────────
// Reads /config.json at runtime so the same build artifact works across
// environments — swap the JSON file at deploy time, no rebuild needed.
let _api = null;

async function getApi() {
  if (_api) return _api;

  const res = await fetch('/config.json');
  const config = await res.json();

  _api = axios.create({ baseURL: config.apiUrl });

  // Attach JWT token to every request
  _api.interceptors.request.use(cfg => {
    const token = localStorage.getItem('ld_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });

  // Redirect to login on 401
  _api.interceptors.response.use(
    res => res,
    err => {
      if (err.response?.status === 401) {
        localStorage.removeItem('ld_token');
        localStorage.removeItem('ld_user');
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
  );

  return _api;
}

// ── Auth ───────────────────────────────────────────────────────────────────
export const authApi = {
  login: async (email, password) => {
    const api = await getApi();
    return api.post('/auth/login', { email, password }).then(r => r.data);
  },
};

// ── Orders ─────────────────────────────────────────────────────────────────
export const ordersApi = {
  getAll: async (params = {}) => {
    const api = await getApi();
    return api.get('/orders', { params }).then(r => r.data);
  },

  getById: async (id) => {
    const api = await getApi();
    return api.get(`/orders/${id}`).then(r => r.data);
  },

  create: async (data) => {
    const api = await getApi();
    return api.post('/orders', data).then(r => r.data);
  },

  update: async (id, data) => {
    const api = await getApi();
    return api.put(`/orders/${id}`, data).then(r => r.data);
  },

  changeStatus: async (id, statusId) => {
    const api = await getApi();
    return api.patch(`/orders/${id}/status`, { statusId }).then(r => r.data);
  },

  assign: async (id, assignedToId) => {
    const api = await getApi();
    return api.patch(`/orders/${id}/assign`, { assignedToId }).then(r => r.data);
  },

  // Borrowers
  getBorrowers: async (id) => {
    const api = await getApi();
    return api.get(`/orders/${id}/borrowers`).then(r => r.data);
  },

  replaceBorrowers: async (id, borrowers) => {
    const api = await getApi();
    return api.put(`/orders/${id}/borrowers`, borrowers).then(r => r.data);
  },

  // Payees (Admin only)
  getPayees: async (id) => {
    const api = await getApi();
    return api.get(`/orders/${id}/payees`).then(r => r.data);
  },

  replacePayees: async (id, payees) => {
    const api = await getApi();
    return api.put(`/orders/${id}/payees`, payees).then(r => r.data);
  },

  // Notes
  addNote: async (id, body) => {
    const api = await getApi();
    return api.post(`/orders/${id}/notes`, { body }).then(r => r.data);
  },

  // Attachments
  getAttachments: async (id) => {
    const api = await getApi();
    return api.get(`/orders/${id}/attachments`).then(r => r.data);
  },

  uploadAttachment: async (id, file, attachmentName) => {
    const api = await getApi();
    const form = new FormData();
    form.append('file', file);
    form.append('attachmentName', attachmentName);
    return api.post(`/orders/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  deleteAttachment: async (id, attId) => {
    const api = await getApi();
    return api.delete(`/orders/${id}/attachments/${attId}`).then(r => r.data);
  },

  downloadAttachment: async (id, attId) => {
    const api = await getApi();
    return api.get(`/orders/${id}/attachments/${attId}/download`, { responseType: 'blob' })
              .then(r => r.data);
  },

  // Audit
  getAuditLog: async (id) => {
    const api = await getApi();
    return api.get(`/orders/${id}/audit`).then(r => r.data);
  },
};

// ── Admin ──────────────────────────────────────────────────────────────────
export const adminApi = {
  // Users
  getUsers: async () => {
    const api = await getApi();
    return api.get('/admin/users').then(r => r.data);
  },

  createUser: async (data) => {
    const api = await getApi();
    return api.post('/admin/users', data).then(r => r.data);
  },

  updateUser: async (id, data) => {
    const api = await getApi();
    return api.patch(`/admin/users/${id}`, data).then(r => r.data);
  },

  updateRole: async (id, role) => {
    const api = await getApi();
    return api.patch(`/admin/users/${id}/role`, { role }).then(r => r.data);
  },

  resetPassword: async (id, newPassword) => {
    const api = await getApi();
    return api.patch(`/admin/users/${id}/password`, { newPassword }).then(r => r.data);
  },

  deactivate: async (id) => {
    const api = await getApi();
    return api.patch(`/admin/users/${id}/deactivate`).then(r => r.data);
  },

  activate: async (id) => {
    const api = await getApi();
    return api.patch(`/admin/users/${id}/activate`).then(r => r.data);
  },

  // Audit
  getAuditLog: async (page = 1, pageSize = 50) => {
    const api = await getApi();
    return api.get('/admin/audit', { params: { page, pageSize } }).then(r => r.data);
  },

  // Reports
  getFinancials: async (params) => {
    const api = await getApi();
    return api.get('/admin/reports/financials', { params }).then(r => r.data);
  },

  getCallReport: async (params) => {
    const api = await getApi();
    return api.get('/admin/reports/callreport', { params }).then(r => r.data);
  },

  getProductivity: async (params) => {
    const api = await getApi();
    return api.get('/admin/reports/productivity', { params }).then(r => r.data);
  },
};

// ── Lookups ────────────────────────────────────────────────────────────────
// Fetched once at login and cached in LookupContext.
// Provides all dropdown values: FileTypes, OrderStatuses, OrigSystems,
// LoanTypes (admin-managed), LienStatuses.
export const lookupsApi = {
  getAll: async () => {
    const api = await getApi();
    return api.get('/lookups').then(r => r.data);
  },

  // Loan type management (Admin only)
  createLoanType: async (name) => {
    const api = await getApi();
    return api.post('/lookups/loantypes', { name }).then(r => r.data);
  },

  updateLoanType: async (id, name) => {
    const api = await getApi();
    return api.put(`/lookups/loantypes/${id}`, { name }).then(r => r.data);
  },

  deleteLoanType: async (id) => {
    const api = await getApi();
    return api.delete(`/lookups/loantypes/${id}`).then(r => r.data);
  },
};
